var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useGetCustomerApi, useGetPersonApi, useIsDietTypeEnabled, } from '@planter/common';
import { useMemo } from 'react';
import { AppRoutes } from '../routes/routes';
import { useIsPlanPreferencesEnabled } from './useIsPlanPreferencesEnabled';
/**
 * Fonte unica di verità sulla sequenza degli step di onboarding.
 *
 * Espone:
 * - `steps`: gli step applicabili all'utente in ordine (diet-type è incluso solo
 *   se abilitato via `useIsDietTypeEnabled`, coerentemente con il flusso guidato);
 * - `firstNotCompletedStep`: il primo step con dati mancanti (o `Plan` se completo);
 * - `isReady`: false finché customer/person sono in caricamento, così i consumer
 *   (es. la guard di rotta) non decidono su uno stato incompleto (es. al refresh,
 *   quando `person` non è ancora arrivato e sembrerebbe che nulla sia compilato.)
 */
export var useOnboardingSteps = function () {
    var customerQuery = useGetCustomerApi();
    var customer = customerQuery.data;
    var personQuery = useGetPersonApi(customer === null || customer === void 0 ? void 0 : customer.personId);
    var person = personQuery.data;
    var isDietTypeEnabled = useIsDietTypeEnabled();
    var isPlanPreferencesEnabled = useIsPlanPreferencesEnabled();
    var steps = useMemo(function () { return __spreadArray(__spreadArray(__spreadArray([
        { route: AppRoutes.OnboardingName, isCompleted: Boolean(person === null || person === void 0 ? void 0 : person.name) },
        {
            route: AppRoutes.OnboardingBirthday,
            isCompleted: Boolean(person === null || person === void 0 ? void 0 : person.birthDate),
        },
        {
            route: AppRoutes.OnboardingGender,
            isCompleted: Boolean(person === null || person === void 0 ? void 0 : person.gender),
        },
        {
            route: AppRoutes.OnboardingHeight,
            isCompleted: Boolean(person === null || person === void 0 ? void 0 : person.height),
        },
        {
            route: AppRoutes.OnboardingWorkout,
            isCompleted: Boolean(person === null || person === void 0 ? void 0 : person.activitiesId),
        }
    ], (isDietTypeEnabled
        ? [
            {
                route: AppRoutes.OnboardingDietType,
                isCompleted: Boolean(person === null || person === void 0 ? void 0 : person.dietType),
            },
        ]
        : []), true), [
        {
            route: AppRoutes.OnboardingAllergies,
            isCompleted: Boolean(person === null || person === void 0 ? void 0 : person.stepAllergies),
        },
        {
            route: AppRoutes.OnboardingDislikedIngredientsPage,
            isCompleted: Boolean(person === null || person === void 0 ? void 0 : person.stepExcludedIngredients),
        }
    ], false), (isPlanPreferencesEnabled
        ? [
            {
                route: AppRoutes.OnboardingPlanPreferences,
                isCompleted: false,
            },
        ]
        : []), true); }, [isDietTypeEnabled, isPlanPreferencesEnabled, person]);
    var firstNotCompletedStep = useMemo(function () { var _a, _b; return (_b = (_a = steps.find(function (step) { return !step.isCompleted; })) === null || _a === void 0 ? void 0 : _a.route) !== null && _b !== void 0 ? _b : AppRoutes.Plan; }, [steps]);
    // `isReady` è false finché customer/person non si sono stabilizzati, così i
    // consumer non decidono su dati stantii. Serve gestire due casi:
    //
    // 1) Caricamento iniziale: attendiamo che la query si risolva. Non ci si può
    //    basare su `isLoading`, che con React Query resta true anche per una query
    //    disabilitata (la query person è disabilitata quando non c'è un personId:
    //    in quel caso non c'è nulla da attendere).
    // 2) Refetch dopo il salvataggio di uno step: completare uno step invalida
    //    Person/Customer (invalidateQueriesForPlan, refetchType 'active') e fa
    //    partire un refetch in background — durante il quale `isSuccess` resta true
    //    ma i dati sono ancora quelli vecchi. Se decidessimo qui, `firstNotCompleted`
    //    sarebbe indietro di uno step e la guard rimbalzerebbe l'utente allo step
    //    appena completato. Attendendo `!isFetching` decidiamo solo su dati freschi.
    // Il personId serve solo per il primo step (createPerson invalida il customer,
    // che al refetch porta il nuovo personId): finché non ce l'abbiamo aspettiamo
    // che il customer si stabilizzi. Con un personId già presente non serve stare
    // dietro ai refetch del customer (il canale B2B non cambia in onboarding), così
    // evitiamo Loader inutili ad ogni step / focus della finestra.
    var hasPersonId = Boolean(customer === null || customer === void 0 ? void 0 : customer.personId);
    var isCustomerSettled = (customerQuery.isSuccess || customerQuery.isError) &&
        (hasPersonId || !customerQuery.isFetching);
    var isPersonSettled = !hasPersonId ||
        ((personQuery.isSuccess || personQuery.isError) && !personQuery.isFetching);
    var isReady = isCustomerSettled && isPersonSettled;
    return { steps: steps, firstNotCompletedStep: firstNotCompletedStep, isReady: isReady };
};
